import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { hotelInfo, getHotelText } from '../config/hotelTexts';
import { useLanguage } from '../context/LanguageContext';

const FooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl} 0;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const FooterSection = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.light};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: 1.2rem;
    position: relative;
    padding-bottom: ${({ theme }) => theme.spacing.sm};
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 50px;
      height: 2px;
      background-color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  a {
    color: ${({ theme }) => theme.colors.light};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const ContactInfo = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: flex-start;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    min-width: 16px;
    color: ${({ theme }) => theme.colors.accent};
    margin-top: 4px; /* Aligner avec la première ligne de texte */
  }

  div {
    display: flex;
    flex-direction: column;
  }

  p {
    margin: 0;
    padding: 0;
    line-height: 1.6;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.light};
  font-size: 0.9rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${({ theme }) => theme.spacing.md};
  padding-right: ${({ theme }) => theme.spacing.md};
`;

const OpeningHoursText = styled.p`
  white-space: pre-line;
`;

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>{getHotelText(language, 'about')}</h3>
          <p>{getHotelText(language, 'shortDescription')}</p>
        </FooterSection>
        
        <FooterSection>
          <h3>{getHotelText(language, 'quickLinks')}</h3>
          <FooterLinks>
            <FooterLink><Link to={`/${language}/`}>{t('nav.home')}</Link></FooterLink>
            <FooterLink><Link to={`/${language}/${language === 'fr' ? 'chambres' : 'rooms'}`}>{t('nav.rooms')}</Link></FooterLink>
            <FooterLink><Link to={`/${language}/restaurant`}>{t('nav.restaurant')}</Link></FooterLink>
            <FooterLink><Link to={`/${language}/${language === 'fr' ? 'alentours' : 'surroundings'}`}>{t('nav.region')}</Link></FooterLink>
            <FooterLink><Link to={`/${language}/contact`}>{t('nav.contact')}</Link></FooterLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <h3>{getHotelText(language, 'contactTitle')}</h3>
          <ContactInfo>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
            <div>
              <p>{hotelInfo.address.street}</p>
              <p>{hotelInfo.address.city}</p>
              <p>{hotelInfo.address.country}</p>
            </div>
          </ContactInfo>
          <ContactInfo>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
            </svg>
            <p>{hotelInfo.contact.phone}</p>
          </ContactInfo>
          <ContactInfo>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z"/>
            </svg>
            <p>{hotelInfo.contact.email}</p>
          </ContactInfo>
        </FooterSection>
        
        <FooterSection>
          <h3>{getHotelText(language, 'openingHoursTitle')}</h3>
          <OpeningHoursText>{getHotelText(language, 'openingHours')}</OpeningHoursText>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        <p>
          &copy; {currentYear} {hotelInfo.name} | {' '}
          <Link to={`/${language}/${language === 'fr' ? 'mentions-legales' : 'legal-notice'}`} style={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {t('footer.legalNotice')}
          </Link>
        </p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
